import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Modal,
  CircularProgress,
  Tooltip,
  Checkbox,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import QueryString from 'qs';
import { getUsersList } from '../../../api/member-management';
import { getMemberGroups } from '../../../api/member-group';
import { getTemporaryUsersList } from '../../../api/temporaryUser';
import { centersDetail } from '../../../api/store-management';
import CommonDatePicker from '../../../form-components/CommonDatePicker';
import useConsultOptions from '../../../hooks/useConsultOptions';
import {
  EditModalContainer,
  EditHeader,
  EditHeaderTitle,
  EditHeaderClose,
  EditBody,
  PoolPanel,
  PanelGap,
  PickedPanel,
  SearchRow,
  SearchField,
  FilterToggleButton,
  ResultRow,
  ResultLabel,
  AddAllButton,
  ListHead,
  ListScroll,
  PoolRow,
  PickedRow,
  RowCheck,
  Cell,
  HeadCell,
  NameCell,
  TypeBadge,
  ConsentText,
  RemoveRowButton,
  EmptyMessage,
  PickedHeader,
  PickedTitle,
  PickedSearchField,
  ExcludeButton,
  ExcelAddButton,
  FilterPanel,
  FilterPanelHeader,
  FilterPanelTitle,
  FilterPanelClose,
  FilterPanelBody,
  FilterSection,
  FilterSectionTitle,
  ChipRow,
  StopDateHeader,
  StopDateRow,
  FilterChip,
  FilterPanelFooter,
  FilterResetButton,
  FilterApplyButton,
  UndoBar,
  UndoLabel,
  UndoAction,
  EditFooter,
  SummaryText,
  SummaryStrong,
  SummaryMuted,
  FooterActions,
  CancelButton,
  ApplyButton,
} from './MemberRecipientPickerModalStyled';

// ============================================================
// 상수
// ============================================================

// 조회 상한. 회원목록의 전체선택이 쓰는 값과 동일하게 맞춘다.
const FETCH_LIMIT = 10000;

// 한 번에 그리는 행 수. 목록을 통째로 그리면 수천 행에서 스크롤이 끊긴다.
const RENDER_CHUNK = 120;

// useQuery data 기본값. 인라인 [] 리터럴을 쓰면 data 가 undefined 인 동안(모달 닫힘 등)
// 렌더마다 새 참조가 생겨 poolRows 를 deps 로 둔 effect 가 무한 setState 루프에 빠진다.
const EMPTY_POOL = [];

const MEMBER_TYPE_CHIPS = [
  { value: null, label: '전체' },
  { value: 'ACTIVE', label: '유효회원' },
  { value: 'SCHEDULED', label: '사용예정' },
  { value: 'EXPIRING_SOON', label: '만료임박' },
  { value: 'STOPPED', label: '중지회원' },
  { value: 'UNPAID', label: '미수회원' },
  { value: 'LOCKER_EXPIRING', label: '라커만료' },
  { value: 'EXPIRED', label: '만료회원' },
];

const TICKET_TYPE_CHIPS = [
  { value: null, label: '전체' },
  { value: 'COURSE_TICKET', label: '수강권' },
  { value: 'COURSE_PASS', label: '이용권' },
  { value: 'TRIAL_TICKET', label: '일일권' },
];

const GENDER_CHIPS = [
  { value: null, label: '전체' },
  { value: 'MALE', label: '남자' },
  { value: 'FEMALE', label: '여자' },
];

const SMS_CHIPS = [
  { value: null, label: '전체' },
  { value: true, label: '동의' },
  { value: false, label: '미동의' },
];

const AGE_RANGE_CHIPS = [
  { value: null, label: '전체' },
  { value: 'UNDER_30', label: '30세 미만' },
  { value: 'AGE_30_40', label: '30~40세' },
  { value: 'AGE_40_50', label: '40~50세' },
  { value: 'AGE_50_60', label: '50~60세' },
  { value: 'OVER_60', label: '60세 이상' },
];

const CONVERSION_RATE_CHIPS = [
  { value: null, label: '전체' },
  { value: 'VERY_HIGH', label: '아주높음' },
  { value: 'HIGH', label: '높음' },
  { value: 'MEDIUM', label: '중간' },
  { value: 'LOW', label: '낮음' },
  { value: 'VERY_LOW', label: '아주낮음' },
];

const SPORT_TYPE_LABELS = {
  FITNESS: '피트니스 / P.T',
  PILATES: '필라테스',
  BARRE_PILATES: '바레 필라테스',
  GOLF: '골프',
  YOGA: '요가',
  TANNING: '태닝',
  STRETCHING: '스트레칭',
};

const MEMBER_TYPE_TONE = {
  유효회원: '#00838F',
  사용예정: '#3564D6',
  만료임박: '#C46A00',
  중지회원: '#C2403D',
  미수회원: '#F49215',
  라커만료: '#8A939C',
  만료회원: '#8A939C',
};

const EMPTY_FILTERS = {
  // 백엔드가 List 파라미터로 받는 필터라 다중 선택을 지원한다.
  memberType: [],
  exerciseType: [],
  ticketType: [],
  gender: [],
  smsReceive: [],
  ageRange: null,
  conversionRate: null,
  visitPath: null,
  consultMethod: null,
  exercisePurpose: null,
  // 중지 종료일 범위. 시작/종료를 한 덩어리로 들고 다녀야 적용 개수가 1개로 세어진다.
  memberStopDateRange: null,
};

// ============================================================
// 순수 헬퍼
// ============================================================

const digitsOf = value => String(value ?? '').replace(/[^0-9]/g, '');

// DatePicker 가 넘기는 Date 를 로컬 기준 YYYY-MM-DD 로 굳힌다.
// toISOString 을 쓰면 UTC 로 밀려 하루 전 날짜가 서버로 나간다.
const toYmd = date => {
  if (!date || isNaN(new Date(date).getTime())) return '';
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

const genderLabel = value => {
  if (value === 'MALE') return '남자';
  if (value === 'FEMALE') return '여자';
  return value || '-';
};

// 발송 로직이 원본 객체(ticketName·branchName·lastPaymentDate 등)를 그대로 쓰므로
// 표시용 값만 파생하고 원본은 raw로 들고 다닌다.
// consentSource: 'sms'(문자 수신 동의, 기본) | 'push'(회원앱 알림 동의) — 호출부(문자/알림)에 맞는
// 값을 보여준다. 목록 API는 두 값 모두 '동의'/'미동의' 한글 문자열로 내려준다.
const normalizeMember = (raw, consentSource = 'sms') => {
  const consentRaw = consentSource === 'push' ? raw.pushYn : raw.isSmsReceive;
  return {
    id: raw.memberId,
    raw,
    name: raw.name || '-',
    phone: raw.phoneNumber || '-',
    gender: genderLabel(raw.gender),
    ageLabel: raw.age ? `${raw.age}세` : '-',
    typeLabel: raw.memberType || '-',
    consent: consentRaw === '동의' ? true : consentRaw === '미동의' ? false : null,
  };
};

const normalizeTemporary = raw => ({
  id: raw.userId,
  raw,
  name: raw.name || '-',
  phone: raw.phoneNumber || '-',
  gender: genderLabel(raw.gender),
  ageLabel: raw.age ? `${raw.age}세` : '-',
  typeLabel: raw.registeredDate || '-',
  // 잠재회원 목록은 수신동의를 smsReceiveYn('Y'/'N')으로 내려준다 (회원목록의 isSmsReceive와 다른 키).
  consent: raw.smsReceiveYn === 'Y' ? true : raw.smsReceiveYn === 'N' ? false : null,
});

// ---- 엑셀 업로드 ----
// 필수: 이름·전화번호 컬럼(헤더명 유연 매칭). 성별/나이/구분/수신동의는 있으면 반영, 없으면 기본값.
const findHeaderKey = (keys, candidates) =>
  keys.find(key => candidates.some(candidate => String(key).replace(/\s/g, '').includes(candidate)));

const parseExcelRecipients = rows => {
  if (!rows.length) return { ok: [], skipped: 0, missingColumns: true };
  const keys = Object.keys(rows[0]);
  const nameKey = findHeaderKey(keys, ['이름', '성명', 'name']);
  const phoneKey = findHeaderKey(keys, ['전화', '휴대', '연락처', 'phone']);
  if (!nameKey || !phoneKey) return { ok: [], skipped: 0, missingColumns: true };
  const genderKey = findHeaderKey(keys, ['성별', 'gender']);
  const ageKey = findHeaderKey(keys, ['나이', '연령', 'age']);
  const typeKey = findHeaderKey(keys, ['구분', 'type']);
  const consentKey = findHeaderKey(keys, ['수신', '동의']);

  const ok = [];
  let skipped = 0;
  rows.forEach(row => {
    const name = String(row[nameKey] ?? '').trim();
    const digits = digitsOf(row[phoneKey]);
    if (!name || digits.length < 10) {
      skipped += 1;
      return;
    }
    const phone = digits.replace(/(\d{3})(\d{3,4})(\d{4})/, '$1-$2-$3');
    const genderRaw = String(row[genderKey] ?? '');
    const consentRaw = String(row[consentKey] ?? '').replace(/\s/g, '');
    const consent = consentRaw
      ? /미동의|거부|N|X|아니/i.test(consentRaw)
        ? '미동의'
        : '동의'
      : undefined;
    const memberType = typeKey ? String(row[typeKey] ?? '').trim() || '엑셀' : '엑셀';
    const id = `excel-${digits}`;
    // 발송 로직은 phoneNumber만 필수로 쓴다. 회원/잠재 정규화가 모두 읽을 수 있게 두 키를 같이 채운다.
    ok.push({
      memberId: id,
      userId: id,
      name,
      phoneNumber: phone,
      gender: /남|M/i.test(genderRaw) ? 'MALE' : /여|F/i.test(genderRaw) ? 'FEMALE' : undefined,
      age: ageKey ? digitsOf(row[ageKey]) || undefined : undefined,
      memberType,
      registeredDate: memberType,
      isSmsReceive: consent,
      smsReceiveYn: consent === '동의' ? 'Y' : consent === '미동의' ? 'N' : undefined,
    });
  });
  return { ok, skipped, missingColumns: false };
};

const matchesKeyword = (row, keyword) => {
  if (!keyword) return true;
  const numeric = digitsOf(keyword);
  return row.name.includes(keyword) || (!!numeric && digitsOf(row.phone).includes(numeric));
};

// ============================================================
// 컴포넌트
// ============================================================

const MemberRecipientPickerModal = ({
  open,
  onClose,
  onApply,
  mode = 'MEMBER',
  initialRecipients = [],
  title = '문자 수신자 편집',
  // SMS는 발송 건당 토큰이 차감돼 예상 차감/잔여 건수를 보여준다. 푸시처럼 비용 개념이
  // 없는 채널에서 호출할 땐 false로 넘겨 그 줄 자체를 숨긴다.
  showCost = true,
  // 회원앱 알림에서는 성별 조건이 필요 없다는 요청으로 이 필터를 숨길 수 있게 함
  showGenderFilter = true,
  // '수신 동의' 컬럼이 어떤 값을 보여줄지 — 'sms'(문자, 기본) | 'push'(회원앱 알림)
  consentSource = 'sms',
  perCost = 1,
  balance = null,
}) => {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const isMemberMode = mode === 'MEMBER';
  // 문자는 전화번호가 없으면 발송 자체가 거절되므로 담을 때 걸러낸다. 회원앱 알림(push)은 번호와 무관하다.
  const requirePhone = consentSource === 'sms';
  // 담으려는 회원 중 전화번호 없는 회원이 있을 때 띄우는 안내. { withPhone, noPhone } 행 목록.
  const [noPhoneNotice, setNoPhoneNotice] = useState(null);
  const normalize = useMemo(
    () => (isMemberMode ? raw => normalizeMember(raw, consentSource) : normalizeTemporary),
    [isMemberMode, consentSource],
  );
  // 방문경로/상담수단/운동목적 옵션은 백엔드 enum이 단일 소스다.
  const { visitPaths, consultMethods, exercisePurposes } = useConsultOptions();

  // 담긴 수신자. id → 원본 객체
  const [picked, setPicked] = useState(() => new Map());
  // 담기/제외 이력. 되돌리기용으로 원본 객체까지 들고 있는다.
  const [history, setHistory] = useState([]);

  const [searchInput, setSearchInput] = useState('');
  const [keyword, setKeyword] = useState(''); // debounce 확정된 검색어
  const [filters, setFilters] = useState(EMPTY_FILTERS); // 조회에 반영된 필터
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  /*
   * 그룹 불러오기 — 회원 그룹을 골라 그 소속만 좌측 목록에 띄운다. 조회 조건 하나로 붙이므로
   * 검색어·필터와 함께 걸 수 있고, 담기는 기존 체크 흐름을 그대로 쓴다.
   * 잠재회원 모드에는 그룹이 없다(그룹은 회원만 담는다).
   */
  const [groupId, setGroupId] = useState(null);
  const [groupAnchor, setGroupAnchor] = useState(null); // 패널에서 편집 중인 필터
  const [filterOpen, setFilterOpen] = useState(false);
  const [pickedKeyword, setPickedKeyword] = useState('');
  // 최종 수신자 클라이언트 필터 (담긴 목록 안에서 걸러보기)
  const EMPTY_PICKED_FILTERS = { type: null, gender: null, consent: null };
  const [pickedFilters, setPickedFilters] = useState(EMPTY_PICKED_FILTERS);
  const [pickedFilterOpen, setPickedFilterOpen] = useState(false);
  const [poolRenderCount, setPoolRenderCount] = useState(RENDER_CHUNK);
  const [pickedRenderCount, setPickedRenderCount] = useState(RENDER_CHUNK);

  const idOf = isMemberMode ? m => m.memberId : m => m.userId;
  const consentLabel = consentSource === 'push' ? t('알림 동의') : t('수신 동의');
  // 좌측 목록의 체크 상태. 담기 버튼을 눌러야 우측으로 이동한다.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // 드래그 다중 선택 — 드래그 시작 시의 목표 상태(선택/해제)를 기억해 지나가는 행에 적용
  const dragModeRef = useRef(null);
  const excelInputRef = useRef(null);
  const [excelNotice, setExcelNotice] = useState('');

  const handleExcelFile = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    try {
      const workbook = XLSX.read(await file.arrayBuffer());
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      const { ok, skipped, missingColumns } = parseExcelRecipients(rows);
      if (missingColumns) {
        setExcelNotice(t('이름·전화번호 컬럼을 찾지 못했습니다'));
        return;
      }
      const fresh = ok.filter(row => !picked.has(idOf(row)));
      if (!fresh.length) {
        setExcelNotice(t('추가할 수신자가 없습니다 (전부 중복이거나 형식 오류)'));
        return;
      }
      applyEdit(
        'add',
        fresh.map(raw => ({ id: idOf(raw), raw, name: raw.name })),
        `${t('엑셀에서')} ${fresh.length}${t('명을 담았습니다')}${skipped ? ` (${t('형식 오류')} ${skipped}${t('건 제외')})` : ''}`,
      );
      setExcelNotice('');
    } catch (error) {
      setExcelNotice(t('엑셀 파일을 읽지 못했습니다'));
    }
  };

  // 모달을 열 때마다 현재 수신자로 초기화한다. 취소하면 아무것도 남지 않는다.
  useEffect(() => {
    if (!open) return;
    setPicked(new Map(initialRecipients.map(m => [idOf(m), m])));
    setHistory([]);
    setSearchInput('');
    setKeyword('');
    setFilters(EMPTY_FILTERS);
    setDraftFilters(EMPTY_FILTERS);
    setFilterOpen(false);
    setPickedKeyword('');
    setSelectedIds(new Set());
    setPickedFilters({ type: null, gender: null, consent: null });
    setPickedFilterOpen(false);
    setExcelNotice('');
    // 열릴 때 한 번만 초기화한다. 편집 중 부모의 수신자가 바뀌어도 덮어쓰지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 드래그 선택 종료 (마우스를 어디서 떼든)
  useEffect(() => {
    const stopDrag = () => {
      dragModeRef.current = null;
    };
    window.addEventListener('mouseup', stopDrag);
    return () => window.removeEventListener('mouseup', stopDrag);
  }, []);

  // 검색어 debounce
  useEffect(() => {
    const timer = setTimeout(() => setKeyword(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const branchId = user?.branch?.branchId;
  const headBranchId = user?.headBranch?.headBranchId;
  // 지점 소속 계정이 branchId 없이 조회하면 타 지점 회원까지 딸려온다. 목록 화면과 같은 가드.
  const canFetch = !!headBranchId && (user?.centerType !== 'BRANCH' || !!branchId);

  // ---- 지점별 운동 종목 (회원 모드 필터용) ----
  const { data: sportTypes = [] } = useQuery({
    queryKey: ['smsRecipientSportTypes', user?.branch?.centerId],
    queryFn: async () => {
      const response = await centersDetail(user?.token, user?.branch?.centerId);
      return response?.branchDetailInfo?.sportTypes || [];
    },
    enabled: open && isMemberMode && !!user?.token && !!user?.branch?.centerId,
  });

  // ---- 좌측 pool 조회 ----
  const poolQuery = useMemo(() => {
    const base = { page: 1, limit: FETCH_LIMIT, headBranchId, branchId, searchInput: keyword };
    if (!isMemberMode) {
      return {
        ...base,
        genders: filters.gender,
        ageRanges: filters.ageRange ? [filters.ageRange] : [],
        conversionRate: filters.conversionRate || '',
        visitPath: filters.visitPath || '',
        consultMethod: filters.consultMethod || '',
        exercisePurpose: filters.exercisePurpose || '',
        sortBy: 'registeredDate',
        sortDirection: 'DESC',
      };
    }
    return {
      ...base,
      memberTypes: filters.memberType,
      exerciseTypes: filters.exerciseType,
      ticketTypes: filters.ticketType,
      genders: filters.gender,
      smsReceiveList: filters.smsReceive,
      memberStopDateFrom: filters.memberStopDateRange?.from || '',
      memberStopDateTo: filters.memberStopDateRange?.to || '',
      memberGroupId: groupId || '',
      sortBy: 'name',
      sortDirection: 'ASC',
    };
  }, [isMemberMode, headBranchId, branchId, keyword, filters, groupId]);

  const { data: groups = EMPTY_POOL } = useQuery({
    queryKey: ['memberGroups', branchId],
    queryFn: () => getMemberGroups(user.token, branchId),
    enabled: open && isMemberMode && !!branchId && !!user?.token,
  });

  const selectedGroup = groups.find(group => group.id === groupId) || null;

  const {
    data: poolRows = EMPTY_POOL,
    isFetching: poolFetching,
    isLoading: poolLoading,
    isError: poolError,
  } = useQuery({
    queryKey: ['smsRecipientPool', mode, poolQuery],
    queryFn: async () => {
      const queryString = QueryString.stringify(poolQuery);
      const result = isMemberMode
        ? await getUsersList(user.token, queryString)
        : await getTemporaryUsersList(user.token, queryString);
      return (result?.content || []).map(normalize);
    },
    enabled: open && !!user?.token && canFetch,
    // 편집 도중 포커스 복귀로 재조회되면 스크롤 위치와 렌더 청크가 되감긴다.
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (open) return;
    setGroupId(null);
    setGroupAnchor(null);
  }, [open]);

  // 조회 결과가 바뀌면 렌더 청크·선택을 처음으로 되돌린다.
  useEffect(() => {
    setPoolRenderCount(RENDER_CHUNK);
    setSelectedIds(new Set());
  }, [poolRows]);

  // ---- 담긴 수신자 ----
  const pickedRows = useMemo(() => [...picked.values()].map(normalize), [picked, normalize]);

  const pickedFilterActive =
    pickedFilters.type !== null || pickedFilters.gender !== null || pickedFilters.consent !== null;

  // 담긴 목록에 실제로 존재하는 구분 값으로 칩을 만든다
  const pickedTypeOptions = useMemo(
    () => [...new Set(pickedRows.map(row => row.typeLabel).filter(label => label && label !== '-'))],
    [pickedRows],
  );

  const visiblePickedRows = useMemo(() => {
    const keywordTrimmed = pickedKeyword.trim();
    return pickedRows.filter(row => {
      if (keywordTrimmed && !matchesKeyword(row, keywordTrimmed)) return false;
      if (pickedFilters.type !== null && row.typeLabel !== pickedFilters.type) return false;
      if (pickedFilters.gender !== null && row.gender !== pickedFilters.gender) return false;
      if (pickedFilters.consent !== null && row.consent !== pickedFilters.consent) return false;
      return true;
    });
  }, [pickedRows, pickedKeyword, pickedFilters]);

  useEffect(() => setPickedRenderCount(RENDER_CHUNK), [pickedKeyword, pickedFilters]);

  const notConsented = useMemo(() => pickedRows.filter(row => row.consent === false), [pickedRows]);

  const unpickedPoolRows = useMemo(() => poolRows.filter(row => !picked.has(row.id)), [poolRows, picked]);

  // ---- 편집 ----
  const applyEdit = (kind, rows, label) => {
    if (!rows.length) return;
    setPicked(prev => {
      const next = new Map(prev);
      rows.forEach(row => (kind === 'add' ? next.set(row.id, row.raw) : next.delete(row.id)));
      return next;
    });
    setHistory(prev => [...prev, { kind, rows, label }].slice(-30));
  };

  const undo = () => {
    const last = history[history.length - 1];
    if (!last) return;
    setPicked(prev => {
      const next = new Map(prev);
      last.rows.forEach(row => (last.kind === 'add' ? next.delete(row.id) : next.set(row.id, row.raw)));
      return next;
    });
    setHistory(prev => prev.slice(0, -1));
  };

  const setRowSelected = (rowId, on) =>
    setSelectedIds(prev => {
      if (prev.has(rowId) === on) return prev;
      const next = new Set(prev);
      if (on) next.add(rowId);
      else next.delete(rowId);
      return next;
    });

  // 클릭(=mousedown)은 토글, 드래그로 지나가는 행은 시작 행과 같은 상태로 맞춘다
  const handleRowMouseDown = row => {
    const willSelect = !selectedIds.has(row.id);
    dragModeRef.current = willSelect;
    setRowSelected(row.id, willSelect);
  };

  const handleRowMouseEnter = row => {
    if (dragModeRef.current === null) return;
    setRowSelected(row.id, dragModeRef.current);
  };

  const toggleAllPoolRows = () => {
    if (!unpickedPoolRows.length) return;
    const allSelected = unpickedPoolRows.every(row => selectedIds.has(row.id));
    setSelectedIds(allSelected ? new Set() : new Set(unpickedPoolRows.map(row => row.id)));
  };

  const addSelectedRows = () => {
    const rows = unpickedPoolRows.filter(row => selectedIds.has(row.id));
    if (!rows.length) return;
    const noPhone = requirePhone ? rows.filter(row => !digitsOf(row.raw.phoneNumber)) : [];
    if (noPhone.length) {
      setNoPhoneNotice({ withPhone: rows.filter(row => digitsOf(row.raw.phoneNumber)), noPhone });
      return;
    }
    applyEdit('add', rows, `${t('선택한')} ${rows.length}${t('명을 담았습니다')}`);
    setSelectedIds(new Set());
  };

  // 안내 모달에서 '제외하고 담기' — 번호 있는 회원만 담는다
  const addRowsExceptNoPhone = () => {
    const { withPhone, noPhone } = noPhoneNotice;
    applyEdit(
      'add',
      withPhone,
      `${withPhone.length}${t('명을 담았습니다')} (${t('전화번호 없음')} ${noPhone.length}${t('명 제외')})`,
    );
    setSelectedIds(new Set());
    setNoPhoneNotice(null);
  };

  const excludeNotConsented = () => {
    if (!notConsented.length) return;
    applyEdit('remove', notConsented, `${t('수신 미동의')} ${notConsented.length}${t('명을 제외했습니다')}`);
  };

  // ---- 필터 패널 ----
  const activeFilterCount = Object.values(filters).filter(value =>
    Array.isArray(value) ? value.length > 0 : value !== null,
  ).length;

  const openFilterPanel = () => {
    setDraftFilters(filters);
    setFilterOpen(true);
  };

  const applyDraftFilters = () => {
    setFilters(draftFilters);
    setFilterOpen(false);
  };

  const pickDraft = (key, value) => setDraftFilters(prev => ({ ...prev, [key]: value }));

  // 다중 선택 칩: "전체"는 배열을 비우고, 그 외는 목록에 있으면 빼고 없으면 더한다.
  const toggleDraftMulti = (key, value) =>
    setDraftFilters(prev => {
      const current = prev[key] || [];
      const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      return { ...prev, [key]: next };
    });

  const stopRange = draftFilters.memberStopDateRange;

  const renderChips = (key, chips, { multi = false } = {}) => (
    <ChipRow>
      {chips.map(chip => {
        const isAllChip = chip.value === null;
        const active = multi
          ? isAllChip
            ? (draftFilters[key] || []).length === 0
            : (draftFilters[key] || []).includes(chip.value)
          : draftFilters[key] === chip.value;
        return (
          <FilterChip
            key={isAllChip ? 'all' : String(chip.value)}
            active={active}
            onClick={() =>
              multi ? (isAllChip ? pickDraft(key, []) : toggleDraftMulti(key, chip.value)) : pickDraft(key, chip.value)
            }>
            {t(chip.label)}
          </FilterChip>
        );
      })}
    </ChipRow>
  );

  // ---- 스크롤 끝에 닿으면 더 그린다 ----
  const handleScroll = (event, total, count, setCount) => {
    if (count >= total) return;
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200) setCount(prev => prev + RENDER_CHUNK);
  };

  const handleApply = () => {
    onApply([...picked.values()]);
    onClose();
  };

  const selectedVisibleCount = unpickedPoolRows.filter(row => selectedIds.has(row.id)).length;

  const lastHistory = history[history.length - 1];
  const typeColumnLabel = isMemberMode ? '구분' : '등록일';

  return (
    <Modal open={open} onClose={onClose}>
      <EditModalContainer>
        <EditHeader>
          <EditHeaderTitle>{t(title)}</EditHeaderTitle>
          <EditHeaderClose onClick={onClose}>✕</EditHeaderClose>
        </EditHeader>

        <EditBody>
          {/* ---------------- 왼쪽: 회원 찾기 ---------------- */}
          <PoolPanel>
            <SearchRow>
              <SearchField>
                <span style={{ fontSize: '14px', color: '#a6a6a6' }}>🔍</span>
                <input
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  placeholder={t('이름 또는 전화번호를 입력해 주세요')}
                />
              </SearchField>
              <FilterToggleButton active={activeFilterCount > 0} onClick={openFilterPanel}>
                <FilterListIcon sx={{ fontSize: 14 }} />
                {activeFilterCount > 0 ? `${t('필터')} ${activeFilterCount}${t('개 적용중')}` : t('필터 검색')}
              </FilterToggleButton>
            </SearchRow>

            <ResultRow>
              <ResultLabel>
                {poolFetching
                  ? t('조회 중...')
                  : poolError
                    ? t('조회에 실패했습니다')
                    : `${t('조건 결과')} ${poolRows.length}${t('명')}`}
              </ResultLabel>
              <div style={{ flex: 1 }} />
              {/* 그룹으로 좁혀 담기 — 회원 모드에서만. 잠재회원에는 그룹이 없다. */}
              {isMemberMode && (
                <AddAllButton
                  onClick={e => setGroupAnchor(e.currentTarget)}
                  sx={{
                    backgroundColor: groupId ? '#E4F5F6' : '#fff',
                    color: groupId ? '#0F7F85' : '#4B5563',
                    border: `1px solid ${groupId ? '#4AB3BC' : '#DDE2E5'}`,
                    marginRight: '6px',
                    transition: 'background-color 150ms ease, border-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
                    '&:hover': {
                      backgroundColor: groupId ? '#d5eff0' : 'rgba(74, 179, 188, 0.08)',
                      borderColor: '#4AB3BC',
                    },
                    '&:active': {
                      transform: 'scale(0.97)',
                    },
                  }}>
                  <GroupsOutlinedIcon sx={{ fontSize: 15, marginRight: '4px' }} />
                  {selectedGroup ? selectedGroup.name : t('그룹 불러오기')}
                </AddAllButton>
              )}
              <AddAllButton disabled={selectedVisibleCount === 0} onClick={addSelectedRows}>
                {`${t('선택')} ${selectedVisibleCount}${t('명 담기')}`}
              </AddAllButton>
            </ResultRow>

            {/* 그룹 선택 — 고르면 좌측 목록이 그 그룹 소속으로 좁혀진다. 담기는 기존 체크 흐름 그대로. */}
            <Menu
              open={!!groupAnchor}
              anchorEl={groupAnchor}
              onClose={() => setGroupAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    marginTop: '4px',
                    minWidth: '220px',
                    maxHeight: '320px',
                    borderRadius: '10px',
                    border: '1px solid #E4E8EB',
                    boxShadow: '0 10px 28px rgba(0,0,0,0.14)',
                  },
                },
              }}>
              <MenuItem
                selected={!groupId}
                onClick={() => {
                  setGroupId(null);
                  setGroupAnchor(null);
                }}
                sx={{ fontSize: '13px' }}>
                {t('전체 회원')}
              </MenuItem>
              {groups.length === 0 ? (
                <MenuItem disabled sx={{ fontSize: '12.5px' }}>
                  {t('만들어 둔 그룹이 없습니다')}
                </MenuItem>
              ) : (
                groups.map(group => (
                  <MenuItem
                    key={group.id}
                    selected={groupId === group.id}
                    onClick={() => {
                      setGroupId(group.id);
                      setGroupAnchor(null);
                    }}
                    sx={{ fontSize: '13px', display: 'flex', gap: '8px' }}>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {group.name}
                    </span>
                    <span style={{ flexShrink: 0, fontSize: '11.5px', color: '#8A939C' }}>
                      {group.memberCount}
                      {t('명')}
                    </span>
                  </MenuItem>
                ))
              )}
            </Menu>

            <ListHead>
              <RowCheck
                checked={unpickedPoolRows.length > 0 && selectedVisibleCount === unpickedPoolRows.length}
                onClick={toggleAllPoolRows}
                sx={{ cursor: 'pointer' }}>
                {unpickedPoolRows.length > 0 && selectedVisibleCount === unpickedPoolRows.length ? '✓' : ''}
              </RowCheck>
              <HeadCell w="72px">{t('이름')}</HeadCell>
              <HeadCell w="36px">{t('성별')}</HeadCell>
              <HeadCell w="40px">{t('나이')}</HeadCell>
              <HeadCell w="64px">{t(typeColumnLabel)}</HeadCell>
              <HeadCell grow>{t('전화번호')}</HeadCell>
              <HeadCell w="56px">{consentLabel}</HeadCell>
            </ListHead>

            <ListScroll
              onScroll={e => handleScroll(e, unpickedPoolRows.length, poolRenderCount, setPoolRenderCount)}>
              {poolLoading ? (
                <EmptyMessage>
                  <CircularProgress size={20} sx={{ color: '#4ab3bc' }} />
                </EmptyMessage>
              ) : poolRows.length === 0 ? (
                <EmptyMessage>{t('조건에 맞는 회원이 없습니다')}</EmptyMessage>
              ) : unpickedPoolRows.length === 0 ? (
                <EmptyMessage>{t('조건 결과가 모두 담겼습니다')}</EmptyMessage>
              ) : (
                unpickedPoolRows.slice(0, poolRenderCount).map((row, index) => {
                  const isSelected = selectedIds.has(row.id);
                  return (
                    <PoolRow
                      key={row.id}
                      odd={index % 2 === 1}
                      onMouseDown={() => handleRowMouseDown(row)}
                      onMouseEnter={() => handleRowMouseEnter(row)}
                      style={{ userSelect: 'none' }}>
                      <RowCheck checked={isSelected}>{isSelected ? '✓' : ''}</RowCheck>
                      <NameCell w="72px" center>{row.name}</NameCell>
                      <Cell w="36px" center>{row.gender}</Cell>
                      <Cell w="40px" center>{row.ageLabel}</Cell>
                      {isMemberMode ? (
                        <Cell w="64px" sx={{ display: 'flex', justifyContent: 'center' }}>
                          <TypeBadge tone={MEMBER_TYPE_TONE[row.typeLabel] || '#8A939C'}>
                            {row.typeLabel}
                          </TypeBadge>
                        </Cell>
                      ) : (
                        <Cell w="64px" center>{row.typeLabel}</Cell>
                      )}
                      <Cell grow center>{row.phone}</Cell>
                      <ConsentText w="56px" center agreed={row.consent !== false}>
                        {row.consent === null ? '-' : row.consent ? t('동의') : t('미동의')}
                      </ConsentText>
                    </PoolRow>
                  );
                })
              )}
            </ListScroll>
          </PoolPanel>

          <PanelGap />

          {/* ---------------- 오른쪽: 최종 수신자 ---------------- */}
          <PickedPanel>
            <PickedHeader>
              <PickedTitle>
                {t('최종 수신자')} <span>{`${pickedRows.length}${t('명')}`}</span>
              </PickedTitle>
              <PickedSearchField>
                <span style={{ fontSize: '12px', color: '#a6a6a6' }}>🔍</span>
                <input
                  value={pickedKeyword}
                  onChange={e => setPickedKeyword(e.target.value)}
                  placeholder={t('담긴 수신자에서 찾기')}
                />
              </PickedSearchField>
              <FilterToggleButton
                active={pickedFilterActive}
                onClick={() => setPickedFilterOpen(prev => !prev)}
                style={{ padding: '0 8px', height: '32px' }}>
                <FilterListIcon sx={{ fontSize: 14 }} />
                {t('필터')}
                <span style={{ fontSize: '9px', marginLeft: '1px' }}>{pickedFilterOpen ? '▲' : '▼'}</span>
              </FilterToggleButton>
              <input
                ref={excelInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={handleExcelFile}
              />
              <Tooltip
                arrow
                placement="bottom"
                title={
                  <div style={{ fontSize: '11.5px', lineHeight: 1.7, padding: '2px 2px' }}>
                    <div style={{ fontWeight: 700, marginBottom: '2px' }}>{t('엑셀 형식 안내')}</div>
                    <div>{t('첫 행은 컬럼 제목이어야 합니다')}</div>
                    <div>
                      {t('필수')}: {t('이름')} · {t('전화번호')}
                    </div>
                    <div>
                      {t('선택')}: {t('성별')} · {t('나이')} · {t('구분')} · {consentLabel}
                    </div>
                    <div>{t("선택 컬럼이 없으면 '-' 로 표시됩니다")}</div>
                  </div>
                }>
                <ExcelAddButton onClick={() => excelInputRef.current?.click()}>
                  {t('엑셀 추가')}
                </ExcelAddButton>
              </Tooltip>
              <ExcludeButton enabled={notConsented.length > 0} onClick={excludeNotConsented}>
                <span style={{ fontSize: '11px' }}>✕</span>
                {notConsented.length ? t('미동의 제외') : t('미동의 없음')}
              </ExcludeButton>
            </PickedHeader>

            {pickedFilterOpen && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 20px 12px',
                }}>
                {pickedTypeOptions.map(type => (
                  <FilterChip
                    key={type}
                    active={pickedFilters.type === type}
                    onClick={() =>
                      setPickedFilters(prev => ({ ...prev, type: prev.type === type ? null : type }))
                    }>
                    {type}
                  </FilterChip>
                ))}
                {['남자', '여자'].map(gender => (
                  <FilterChip
                    key={gender}
                    active={pickedFilters.gender === gender}
                    onClick={() =>
                      setPickedFilters(prev => ({ ...prev, gender: prev.gender === gender ? null : gender }))
                    }>
                    {t(gender)}
                  </FilterChip>
                ))}
                {[
                  { value: true, label: '동의' },
                  { value: false, label: '미동의' },
                ].map(chip => (
                  <FilterChip
                    key={chip.label}
                    active={pickedFilters.consent === chip.value}
                    onClick={() =>
                      setPickedFilters(prev => ({
                        ...prev,
                        consent: prev.consent === chip.value ? null : chip.value,
                      }))
                    }>
                    {t(chip.label)}
                  </FilterChip>
                ))}
                {pickedFilterActive && (
                  <FilterChip
                    active={false}
                    onClick={() => setPickedFilters({ type: null, gender: null, consent: null })}
                    style={{ marginLeft: 'auto', fontWeight: 700 }}>
                    {t('전체보기')} ({pickedRows.length})
                  </FilterChip>
                )}
              </div>
            )}

            {excelNotice && (
              <div style={{ padding: '4px 20px 0', fontSize: '11.5px', color: '#C2403D' }}>{excelNotice}</div>
            )}

            <ListHead sx={{ padding: '6px 20px' }}>
              <HeadCell w="96px">{t('이름')}</HeadCell>
              <HeadCell w="52px">{t('성별')}</HeadCell>
              <HeadCell w="56px">{t('나이')}</HeadCell>
              <HeadCell w="88px">{t(typeColumnLabel)}</HeadCell>
              <HeadCell grow>{t('전화번호')}</HeadCell>
              <HeadCell w="56px">{consentLabel}</HeadCell>
              <HeadCell w="22px" />
            </ListHead>

            <ListScroll
              onScroll={e =>
                handleScroll(e, visiblePickedRows.length, pickedRenderCount, setPickedRenderCount)
              }>
              {pickedRows.length === 0 ? (
                <EmptyMessage>{t('왼쪽에서 회원을 검색해 담아 주세요')}</EmptyMessage>
              ) : visiblePickedRows.length === 0 ? (
                <EmptyMessage>{t('검색과 일치하는 수신자가 없습니다')}</EmptyMessage>
              ) : (
                visiblePickedRows.slice(0, pickedRenderCount).map((row, index) => (
                  <PickedRow key={row.id} odd={index % 2 === 1}>
                    <NameCell w="96px" center>{row.name}</NameCell>
                    <Cell w="52px" center>{row.gender}</Cell>
                    <Cell w="56px" center>{row.ageLabel}</Cell>
                    {isMemberMode ? (
                      <Cell w="88px" sx={{ display: 'flex', justifyContent: 'center' }}>
                        <TypeBadge tone={MEMBER_TYPE_TONE[row.typeLabel] || '#8A939C'}>{row.typeLabel}</TypeBadge>
                      </Cell>
                    ) : (
                      <Cell w="88px" center>{row.typeLabel}</Cell>
                    )}
                    <Cell grow center>{row.phone}</Cell>
                    <ConsentText w="56px" center agreed={row.consent !== false}>
                      {row.consent === null ? '-' : row.consent ? t('동의') : t('미동의')}
                    </ConsentText>
                    <RemoveRowButton
                      onClick={() => applyEdit('remove', [row], `${row.name} ${t('님을 제외했습니다')}`)}>
                      ✕
                    </RemoveRowButton>
                  </PickedRow>
                ))
              )}
            </ListScroll>
          </PickedPanel>

          {/* ---------------- 필터 오버레이 ---------------- */}
          {filterOpen && (
            <FilterPanel>
              <FilterPanelHeader>
                <FilterPanelTitle>{t('조건으로 회원 찾기')}</FilterPanelTitle>
                <FilterPanelClose onClick={() => setFilterOpen(false)}>✕</FilterPanelClose>
              </FilterPanelHeader>
              <FilterPanelBody>
                {isMemberMode && (
                  <>
                    <FilterSection>
                      <FilterSectionTitle>{t('회원 구분')}</FilterSectionTitle>
                      {renderChips('memberType', MEMBER_TYPE_CHIPS, { multi: true })}
                    </FilterSection>
                    <FilterSection>
                      <FilterSectionTitle>{t('운동 종목')}</FilterSectionTitle>
                      {renderChips(
                        'exerciseType',
                        [
                          { value: null, label: '전체' },
                          ...sportTypes.map(type => ({ value: type, label: SPORT_TYPE_LABELS[type] || type })),
                        ],
                        { multi: true },
                      )}
                    </FilterSection>
                    <FilterSection>
                      <FilterSectionTitle>{t('회원권 유형')}</FilterSectionTitle>
                      {renderChips('ticketType', TICKET_TYPE_CHIPS, { multi: true })}
                    </FilterSection>
                    <FilterSection>
                      <StopDateHeader>
                        <Checkbox
                          size="small"
                          sx={{ padding: 0, color: '#4AB3BC', '&.Mui-checked': { color: '#4AB3BC' } }}
                          checked={stopRange !== null}
                          onChange={e => {
                            const today = toYmd(new Date());
                            pickDraft('memberStopDateRange', e.target.checked ? { from: today, to: today } : null);
                          }}
                        />
                        <FilterSectionTitle>{t('회원 중지 종료일')}</FilterSectionTitle>
                      </StopDateHeader>
                      <StopDateRow>
                        <CommonDatePicker
                          sx={{ flex: 1, minWidth: 0 }}
                          value={stopRange?.from || null}
                          onChange={n => pickDraft('memberStopDateRange', { ...stopRange, from: toYmd(n) })}
                          disabled={stopRange === null}
                          placeholder={t('시작일')}
                        />
                        <FilterSectionTitle>~</FilterSectionTitle>
                        <CommonDatePicker
                          sx={{ flex: 1, minWidth: 0 }}
                          value={stopRange?.to || null}
                          onChange={n => pickDraft('memberStopDateRange', { ...stopRange, to: toYmd(n) })}
                          minDate={stopRange?.from || null}
                          disabled={stopRange === null}
                          placeholder={t('종료일')}
                        />
                      </StopDateRow>
                    </FilterSection>
                  </>
                )}
                {showGenderFilter && (
                  <FilterSection>
                    <FilterSectionTitle>{t('성별')}</FilterSectionTitle>
                    {renderChips('gender', GENDER_CHIPS, { multi: true })}
                  </FilterSection>
                )}
                {isMemberMode ? (
                  // 서버 필터는 문자 수신 동의(IS_SMS_RECEIVE) 기준만 있다. 회원앱 알림에서는
                  // 필터링할 방법이 없어 혼동을 줄 수 있으니 이 조건 자체를 숨긴다(컬럼 표시는 유지).
                  consentSource === 'sms' && (
                    <FilterSection>
                      <FilterSectionTitle>{t('문자 수신 동의')}</FilterSectionTitle>
                      {renderChips('smsReceive', SMS_CHIPS, { multi: true })}
                    </FilterSection>
                  )
                ) : (
                  <>
                    <FilterSection>
                      <FilterSectionTitle>{t('연령대')}</FilterSectionTitle>
                      {renderChips('ageRange', AGE_RANGE_CHIPS)}
                    </FilterSection>
                    <FilterSection>
                      <FilterSectionTitle>{t('회원 전환 확률')}</FilterSectionTitle>
                      {renderChips('conversionRate', CONVERSION_RATE_CHIPS)}
                    </FilterSection>
                    <FilterSection>
                      <FilterSectionTitle>{t('방문경로')}</FilterSectionTitle>
                      {renderChips('visitPath', [{ value: null, label: '전체' }, ...visitPaths])}
                    </FilterSection>
                    <FilterSection>
                      <FilterSectionTitle>{t('상담수단')}</FilterSectionTitle>
                      {renderChips('consultMethod', [{ value: null, label: '전체' }, ...consultMethods])}
                    </FilterSection>
                    <FilterSection>
                      <FilterSectionTitle>{t('운동목적')}</FilterSectionTitle>
                      {renderChips('exercisePurpose', [{ value: null, label: '전체' }, ...exercisePurposes])}
                    </FilterSection>
                  </>
                )}
              </FilterPanelBody>
              <FilterPanelFooter>
                <FilterResetButton onClick={() => setDraftFilters(EMPTY_FILTERS)}>
                  {t('초기화')}
                </FilterResetButton>
                <FilterApplyButton onClick={applyDraftFilters}>
                  {t('설정한 조건으로 회원검색')}
                </FilterApplyButton>
              </FilterPanelFooter>
            </FilterPanel>
          )}
        </EditBody>

        {lastHistory && (
          <UndoBar>
            <UndoLabel>{lastHistory.label}</UndoLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <UndoAction onClick={undo}>
                {history.length > 1 ? `${t('되돌리기')} (${history.length})` : t('되돌리기')}
              </UndoAction>
              {/* 되돌리지 않고 최근 이력 1개만 걷어낸다 — 이전 이력이 있으면 바에 이어서 표시 */}
              <UndoAction onClick={() => setHistory(prev => prev.slice(0, -1))} style={{ opacity: 0.75 }}>
                ✕
              </UndoAction>
            </div>
          </UndoBar>
        )}

        <EditFooter>
          <SummaryText>
            <SummaryStrong>{`${t('수신자')} ${pickedRows.length}${t('명')}`}</SummaryStrong>
            {showCost && (
              <>
                <span>·</span>
                <span>{`${t('예상 차감')} ${perCost * pickedRows.length}${t('건')}`}</span>
                {balance !== null && (
                  <>
                    <span>·</span>
                    <SummaryMuted>{`${t('잔여')} ${balance}${t('건')}`}</SummaryMuted>
                  </>
                )}
              </>
            )}
          </SummaryText>
          <FooterActions>
            <CancelButton onClick={onClose}>{t('취소')}</CancelButton>
            <ApplyButton onClick={handleApply}>{`${pickedRows.length}${t('명 적용')}`}</ApplyButton>
          </FooterActions>
        </EditFooter>

        {/* 전화번호 없는 회원 담기 안내 (문자 전용) */}
        <Dialog open={!!noPhoneNotice} onClose={() => setNoPhoneNotice(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontSize: '17px', fontWeight: 700 }}>{t('전화번호가 없는 회원이 있습니다')}</DialogTitle>
          <DialogContent>
            <div style={{ fontSize: '14px', color: '#333', marginBottom: '8px' }}>
              {`${t('선택한 회원 중')} ${noPhoneNotice?.noPhone.length || 0}${t('명은 전화번호가 없어 문자를 받을 수 없습니다.')}`}
            </div>
            <div
              style={{
                fontSize: '13px',
                color: '#555',
                background: '#FFF4F4',
                border: '1px solid #F5C2C2',
                borderRadius: '8px',
                padding: '10px 12px',
                maxHeight: '120px',
                overflowY: 'auto',
                lineHeight: 1.6,
              }}>
              {noPhoneNotice?.noPhone.map(row => row.name).join(', ')}
            </div>
          </DialogContent>
          <DialogActions sx={{ padding: '0 24px 20px' }}>
            <Button onClick={() => setNoPhoneNotice(null)} sx={{ color: '#555' }}>
              {t('취소')}
            </Button>
            <Button
              variant="contained"
              disableElevation
              disabled={!noPhoneNotice?.withPhone.length}
              onClick={addRowsExceptNoPhone}
              sx={{ backgroundColor: '#4ab3bc', '&:hover': { backgroundColor: '#3a9fa8' } }}>
              {`${t('제외하고')} ${noPhoneNotice?.withPhone.length || 0}${t('명 담기')}`}
            </Button>
          </DialogActions>
        </Dialog>
      </EditModalContainer>
    </Modal>
  );
};

export default MemberRecipientPickerModal;
